import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import CustomSelect from '../components/CustomSelect';
import RowActions from '../components/RowActions';

interface Resident {
  id: number;
  name: string;
  photo: string | null;
}

interface CarePlanGoal {
  id: number;
  residentId: number;
  title: string;
  description: string | null;
  category: string;
  targetOutcome: string | null;
  reviewFrequency: string;
  nextReviewDate: string;
  priority: string;
  status: string;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  resident?: Resident;
  createdBy?: { id: number; name: string };
}

interface Evaluation {
  id: number;
  goalId: number;
  evaluationDate: string;
  progressRating: string;
  progressNotes: string | null;
  outcome: string | null;
  challengesFaced: string | null;
  actionsRequired: string | null;
  goalMet: boolean;
  continueGoal: boolean;
  nextReviewDate: string | null;
  evaluatedBy?: { id: number; name: string };
}

interface Stats {
  totalGoals: number;
  activeGoals: number;
  achievedGoals: number;
  dueForReview: number;
}

const CarePlans: React.FC = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'due-reviews' | 'all-goals'>('overview');
  const [goals, setGoals] = useState<CarePlanGoal[]>([]);
  const [dueGoals, setDueGoals] = useState<CarePlanGoal[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<CarePlanGoal | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);

  const [goalForm, setGoalForm] = useState({
    residentId: '',
    title: '',
    description: '',
    category: 'health',
    targetOutcome: '',
    reviewFrequency: 'monthly',
    priority: 'medium',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  const [evaluationForm, setEvaluationForm] = useState({
    progressRating: 'on_track',
    progressNotes: '',
    outcome: '',
    challengesFaced: '',
    actionsRequired: '',
    goalMet: false,
    continueGoal: true
  });

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const categories = [
    { value: 'health', label: 'Health & Wellbeing' },
    { value: 'mobility', label: 'Mobility' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'social', label: 'Social & Emotional' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'personal_care', label: 'Personal Care' },
    { value: 'communication', label: 'Communication' },
    { value: 'safety', label: 'Safety' },
    { value: 'other', label: 'Other' }
  ];

  const progressRatings = [
    { value: 'exceeding', label: 'Exceeding Expectations', color: 'bg-green-100 text-green-800' },
    { value: 'on_track', label: 'On Track', color: 'bg-blue-100 text-blue-800' },
    { value: 'some_progress', label: 'Some Progress', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'no_progress', label: 'No Progress', color: 'bg-orange-100 text-orange-800' },
    { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, residentsRes] = await Promise.all([
        api.get('/care-plans/stats'),
        api.get('/residents/assigned')
      ]);
      setStats(statsRes.data.data);
      setResidents(residentsRes.data.data || []);

      if (activeTab === 'due-reviews') {
        const dueRes = await api.get('/care-plans/due-reviews');
        setDueGoals(dueRes.data.data || []);
      } else if (activeTab === 'all-goals' || activeTab === 'overview') {
        const goalsRes = await api.get('/care-plans/goals');
        setGoals(goalsRes.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch care plan data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/care-plans/residents/${goalForm.residentId}/goals`, {
        title: goalForm.title,
        description: goalForm.description || null,
        category: goalForm.category,
        targetOutcome: goalForm.targetOutcome || null,
        reviewFrequency: goalForm.reviewFrequency,
        priority: goalForm.priority,
        startDate: goalForm.startDate,
        endDate: goalForm.endDate || null
      });
      setShowGoalModal(false);
      setGoalForm({
        residentId: '',
        title: '',
        description: '',
        category: 'health',
        targetOutcome: '',
        reviewFrequency: 'monthly',
        priority: 'medium',
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
      });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create goal');
    }
  };

  const handleRecordEvaluation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGoal) return;
    try {
      await api.post(`/care-plans/goals/${selectedGoal.id}/evaluations`, evaluationForm);
      setShowEvaluationModal(false);
      setEvaluationForm({
        progressRating: 'on_track',
        progressNotes: '',
        outcome: '',
        challengesFaced: '',
        actionsRequired: '',
        goalMet: false,
        continueGoal: true
      });
      setSelectedGoal(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record evaluation');
    }
  };

  const handleViewHistory = async (goal: CarePlanGoal) => {
    setSelectedGoal(goal);
    try {
      const res = await api.get(`/care-plans/goals/${goal.id}/evaluations`);
      setEvaluations(res.data.data || []);
      setShowHistoryModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch evaluation history');
    }
  };

  const openEvaluationModal = (goal: CarePlanGoal) => {
    setSelectedGoal(goal);
    setShowEvaluationModal(true);
  };

  const getCategoryLabel = (category: string) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  const getFrequencyLabel = (freq: string) => {
    const labels: Record<string, string> = {
      weekly: 'Weekly',
      fortnightly: 'Fortnightly',
      monthly: 'Monthly'
    };
    return labels[freq] || freq;
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      achieved: 'bg-primary-100 text-primary-800',
      discontinued: 'bg-gray-100 text-gray-800',
      on_hold: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const isOverdue = (nextReviewDate: string) => {
    return new Date(nextReviewDate) < new Date();
  };

  const isDueSoon = (nextReviewDate: string) => {
    const review = new Date(nextReviewDate);
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    return review <= threeDaysFromNow && review >= new Date();
  };

  const filteredGoals = goals.filter(goal => {
    if (categoryFilter !== 'all' && goal.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && goal.status !== statusFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Care Plans</h1>
          <p className="text-gray-600">Manage care plan goals with periodic evaluations</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowGoalModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Add Goal
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-primary-500">
            <p className="text-sm text-gray-500">Total Goals</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalGoals}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <p className="text-sm text-gray-500">Active Goals</p>
            <p className="text-2xl font-bold text-gray-900">{stats.activeGoals}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <p className="text-sm text-gray-500">Achieved</p>
            <p className="text-2xl font-bold text-gray-900">{stats.achievedGoals}</p>
          </div>
          <div className={`bg-white p-4 rounded-lg shadow border-l-4 ${stats.dueForReview > 0 ? 'border-red-500' : 'border-gray-300'}`}>
            <p className="text-sm text-gray-500">Due for Review</p>
            <p className={`text-2xl font-bold ${stats.dueForReview > 0 ? 'text-red-600' : 'text-gray-900'}`}>{stats.dueForReview}</p>
          </div>
        </div>
      )}

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('due-reviews')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'due-reviews' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Due for Review {stats && stats.dueForReview > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{stats.dueForReview}</span>}
          </button>
          <button
            onClick={() => setActiveTab('all-goals')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'all-goals' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            All Goals
          </button>
        </nav>
      </div>

      {(activeTab === 'overview' || activeTab === 'all-goals') && (
        <div className="mb-4 flex gap-4 flex-wrap">
          <div className="w-full sm:w-60">
            <CustomSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: 'all', label: 'All Categories' },
                ...categories.map(cat => ({ value: cat.value, label: cat.label })),
              ]}
            />
          </div>
          <div className="w-full sm:w-60">
            <CustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'achieved', label: 'Achieved' },
                { value: 'discontinued', label: 'Discontinued' },
              ]}
            />
          </div>
        </div>
      )}

      {activeTab === 'due-reviews' ? (
        <div className="bg-white rounded-lg shadow">
          {dueGoals.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No goals due for review
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {dueGoals.map(goal => (
                <div key={goal.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {goal.resident?.photo ? (
                        <img src={goal.resident.photo} alt={goal.resident.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium">{goal.resident?.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-gray-900">{goal.title}</h3>
                        <p className="text-sm text-gray-500">{goal.resident?.name}</p>
                        <div className="flex gap-2 mt-1">
                          <span className={`px-2 py-0.5 badge-pill ${getPriorityColor(goal.priority)}`}>
                            {goal.priority}
                          </span>
                          <span className="px-2 py-0.5 badge-pill bg-primary-100 text-primary-800">
                            {getFrequencyLabel(goal.reviewFrequency)}
                          </span>
                          <span className={`px-2 py-0.5 badge-pill ${isOverdue(goal.nextReviewDate) ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {isOverdue(goal.nextReviewDate) ? 'Overdue' : 'Due'}: {new Date(goal.nextReviewDate).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <RowActions
                        actions={[
                          { label: 'Evaluate', tone: 'primary', onClick: () => openEvaluationModal(goal) },
                          { label: 'History', tone: 'default', onClick: () => handleViewHistory(goal) },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {filteredGoals.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No care plan goals found
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredGoals.map(goal => (
                <div key={goal.id} className={`p-4 hover:bg-gray-50 ${isOverdue(goal.nextReviewDate) && goal.status === 'active' ? 'bg-red-50' : isDueSoon(goal.nextReviewDate) && goal.status === 'active' ? 'bg-yellow-50' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {goal.resident?.photo ? (
                        <img src={goal.resident.photo} alt={goal.resident.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium">{goal.resident?.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-gray-900">{goal.title}</h3>
                        <p className="text-sm text-gray-500">{goal.resident?.name}</p>
                        {goal.description && (
                          <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className={`px-2 py-0.5 badge-pill ${getStatusColor(goal.status)}`}>
                            {goal.status}
                          </span>
                          <span className="px-2 py-0.5 badge-pill bg-gray-100 text-gray-800">
                            {getCategoryLabel(goal.category)}
                          </span>
                          <span className={`px-2 py-0.5 badge-pill ${getPriorityColor(goal.priority)}`}>
                            {goal.priority}
                          </span>
                          <span className="px-2 py-0.5 badge-pill bg-primary-100 text-primary-800">
                            {getFrequencyLabel(goal.reviewFrequency)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Next review: {new Date(goal.nextReviewDate).toLocaleDateString()}
                          {isOverdue(goal.nextReviewDate) && goal.status === 'active' && (
                            <span className="ml-2 text-red-600 font-medium">OVERDUE</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <RowActions
                        actions={[
                          { label: 'Evaluate', tone: 'primary', hidden: goal.status !== 'active', onClick: () => openEvaluationModal(goal) },
                          { label: 'History', tone: 'default', onClick: () => handleViewHistory(goal) },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showGoalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Add Care Plan Goal</h2>
              <form onSubmit={handleCreateGoal}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resident *</label>
                    <CustomSelect
                      value={goalForm.residentId}
                      onChange={(v) => setGoalForm({ ...goalForm, residentId: v })}
                      placeholder="Select Resident"
                      options={[
                        { value: '', label: 'Select Resident' },
                        ...residents.map(r => ({ value: String(r.id), label: r.name })),
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Goal Title *</label>
                    <input
                      type="text"
                      value={goalForm.title}
                      onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={goalForm.description}
                      onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <CustomSelect
                        value={goalForm.category}
                        onChange={(v) => setGoalForm({ ...goalForm, category: v })}
                        options={categories.map(cat => ({ value: cat.value, label: cat.label }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <CustomSelect
                        value={goalForm.priority}
                        onChange={(v) => setGoalForm({ ...goalForm, priority: v })}
                        options={[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                          { value: 'urgent', label: 'Urgent' },
                        ]}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Frequency *</label>
                    <CustomSelect
                      value={goalForm.reviewFrequency}
                      onChange={(v) => setGoalForm({ ...goalForm, reviewFrequency: v })}
                      options={[
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'fortnightly', label: 'Fortnightly (Every 2 weeks)' },
                        { value: 'monthly', label: 'Monthly' },
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Outcome</label>
                    <textarea
                      value={goalForm.targetOutcome}
                      onChange={(e) => setGoalForm({ ...goalForm, targetOutcome: e.target.value })}
                      rows={2}
                      placeholder="What outcome do we want to achieve?"
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={goalForm.startDate}
                        onChange={(e) => setGoalForm({ ...goalForm, startDate: e.target.value })}
                        className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                      <input
                        type="date"
                        value={goalForm.endDate}
                        onChange={(e) => setGoalForm({ ...goalForm, endDate: e.target.value })}
                        className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowGoalModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Create Goal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEvaluationModal && selectedGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Record Evaluation</h2>
              <p className="text-gray-600 mb-4">Goal: {selectedGoal.title}</p>
              <form onSubmit={handleRecordEvaluation}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Progress Rating *</label>
                    <CustomSelect
                      value={evaluationForm.progressRating}
                      onChange={(v) => setEvaluationForm({ ...evaluationForm, progressRating: v })}
                      options={progressRatings.map(r => ({ value: r.value, label: r.label }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Progress Notes</label>
                    <textarea
                      value={evaluationForm.progressNotes}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, progressNotes: e.target.value })}
                      rows={3}
                      placeholder="Describe progress since last review..."
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Outcome/Observations</label>
                    <textarea
                      value={evaluationForm.outcome}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, outcome: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Challenges Faced</label>
                    <textarea
                      value={evaluationForm.challengesFaced}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, challengesFaced: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Actions Required</label>
                    <textarea
                      value={evaluationForm.actionsRequired}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, actionsRequired: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-3.5">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={evaluationForm.goalMet}
                        onChange={(e) => setEvaluationForm({ ...evaluationForm, goalMet: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Goal Achieved</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={evaluationForm.continueGoal}
                        onChange={(e) => setEvaluationForm({ ...evaluationForm, continueGoal: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Continue Goal</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEvaluationModal(false); setSelectedGoal(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Save Evaluation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && selectedGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedGoal.title}</h2>
                  <p className="text-gray-600">{selectedGoal.resident?.name}</p>
                </div>
                <button
                  onClick={() => { setShowHistoryModal(false); setSelectedGoal(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="ml-2 font-medium">{getCategoryLabel(selectedGoal.category)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className={`ml-2 px-2 py-0.5 badge-pill ${getStatusColor(selectedGoal.status)}`}>{selectedGoal.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Review Frequency:</span>
                    <span className="ml-2 font-medium">{getFrequencyLabel(selectedGoal.reviewFrequency)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Next Review:</span>
                    <span className="ml-2 font-medium">{new Date(selectedGoal.nextReviewDate).toLocaleDateString()}</span>
                  </div>
                </div>
                {selectedGoal.targetOutcome && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-gray-500 text-sm">Target Outcome:</span>
                    <p className="mt-1 text-sm">{selectedGoal.targetOutcome}</p>
                  </div>
                )}
              </div>

              <h3 className="font-medium text-gray-900 mb-3">Evaluation History</h3>
              {evaluations.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No evaluations recorded yet</p>
              ) : (
                <div className="space-y-4">
                  {evaluations.map(evaluation => (
                    <div key={evaluation.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`px-2 py-1 badge-pill ${progressRatings.find(r => r.value === evaluation.progressRating)?.color || 'bg-gray-100 text-gray-800'}`}>
                            {progressRatings.find(r => r.value === evaluation.progressRating)?.label || evaluation.progressRating}
                          </span>
                          {evaluation.goalMet && (
                            <span className="ml-2 px-2 py-1 badge-pill bg-green-100 text-green-800">Goal Achieved</span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">{new Date(evaluation.evaluationDate).toLocaleDateString()}</span>
                      </div>
                      {evaluation.progressNotes && (
                        <p className="text-sm text-gray-700 mt-2">{evaluation.progressNotes}</p>
                      )}
                      {evaluation.outcome && (
                        <p className="text-sm text-gray-600 mt-1"><strong>Outcome:</strong> {evaluation.outcome}</p>
                      )}
                      {evaluation.challengesFaced && (
                        <p className="text-sm text-gray-600 mt-1"><strong>Challenges:</strong> {evaluation.challengesFaced}</p>
                      )}
                      {evaluation.actionsRequired && (
                        <p className="text-sm text-gray-600 mt-1"><strong>Actions:</strong> {evaluation.actionsRequired}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">Evaluated by: {evaluation.evaluatedBy?.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarePlans;
